import { Injectable } from '@angular/core';
import { UserAuthenticationService } from '../../../../../../libs/administration/src/lib/services/user-authentication.service';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { Observable, BehaviorSubject, catchError, filter, finalize, map, switchMap, take, throwError } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../../environments/environment';
import { AnalyticsService, SpinnerService } from '@zayan-erp/shared';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

@Injectable()
export class InterceptorService implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(
    private _authService: UserAuthenticationService,
    private _toaster: ToastrService,
    private _spinnerService: SpinnerService,
    private _modalService: NgbModal,
    private _analyticsService: AnalyticsService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let req = request;
    const ignoreUrls: string[] = [environment.s3Url, environment.s3PublicUrl, '.amazonaws.com'];
    const refreshTokenUrl = `${environment.baseUrl}token/refresh`;
    const isApiCallingV2 = request.url.includes('/api/v2');
    const shouldAddToken =
      request.url !== refreshTokenUrl &&
      this._authService.isLogin() &&
      !ignoreUrls.some(url => url && request.url.includes(url));

    // Add token if needed
    if (shouldAddToken) {
      const idToken = this._authService.getToken();
      req = request.clone({
        headers: request.headers.set('Authorization', `Bearer ${idToken}`)
      });
    }

    return next.handle(req).pipe(
      map((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          // Check if response body has success property set to false
          if (event.body && typeof event.body === 'object' && event.body.success === false && !isApiCallingV2) {
            // Create an HttpErrorResponse from the successful response
            const error = new HttpErrorResponse({
              error: event.body,
              headers: event.headers,
              status: event.body.status || 400,
              statusText: event.body.message || 'Some unknown error has occurred',
              url: event.url || undefined
            });
            throw error;
          }
        }
        return event;
      }),
      catchError((error: HttpErrorResponse) => {
        // 401s are an expected part of the refresh-token flow, so they are not reported as errors
        if (error.status !== 401) {
          this._analyticsService.captureApiError({
            status: error.status,
            url: error.url || request.url || 'Unknown URL Error Code: 10001',
            method: request.method,
            message: error.message,
            statusText: error.statusText || 'Unknown Status Text Error Code: 10002'
          });
        }

        if (error.status === 401) {
          if (request.url === refreshTokenUrl) {
            // This is the refresh token request itself that failed
            this._authService.captureSessionExpired();
            this._toaster.info('Please log in again to continue using the app', 'Your session has expired');
            this._modalService.dismissAll();
            this._authService.logout(true, true);
            return throwError(() => error);
          }
          return this.handle401Error(request, next);
        }

        return throwError(() => error);
      }),
      finalize(() => {
        // if (!isApiCallingV2) {
        this._spinnerService.handleSpinnerStop();
        // }
      })
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this._authService.refreshToken().pipe(
        switchMap((success: boolean) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(success);

          // Retry the original request with new token
          const newToken = this._authService.getToken();
          const newRequest = request.clone({
            headers: request.headers.set('Authorization', `Bearer ${newToken}`)
          });
          return next.handle(newRequest);
        }),
        catchError(error => {
          this.isRefreshing = false;
          this._authService.captureSessionExpired();
          this._toaster.info('Please log in again to continue using the app', 'Your session has expired');
          this._modalService.dismissAll();
          this._authService.logout(true, true);
          return throwError(() => error);
        })
      );
    } else {
      // Wait for the token to be refreshed, then retry the request
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(() => {
          const newToken = this._authService.getToken();
          const newRequest = request.clone({
            headers: request.headers.set('Authorization', `Bearer ${newToken}`)
          });
          return next.handle(newRequest);
        })
      );
    }
  }
}
